import filter from 'lodash/filter';
import find from 'lodash/find';
import reduce from 'lodash/reduce';
import { scriptStepTypes } from '@xmatters/common/constants/scriptSteps';

const { MERGE, RESPONSES } = scriptStepTypes;

export function getMergeSourceId(sourceIdStr, blocksById) {
  const [inputBlockId, portId] = sourceIdStr.split('.');
  const block = blocksById[inputBlockId];
  if (!block) {
    return '';
  }

  if (block.step?.type !== RESPONSES) {
    // this will cover both CREATE_ALERT and init incident
    const port = find(block.ports, { triggerBlockId: portId });
    if (port) {
      return portId;
    }
  }

  return inputBlockId;
}

export function getMergeTargetPort(sourceIdStr, blocks) {
  const [inputBlockId, portId] = sourceIdStr.split('.');
  const portIdBlock = find(blocks, { id: portId });
  if (portIdBlock?.groupingBlockId === inputBlockId) {
    return portId;
  }
  return inputBlockId;
}

const getMergeBlockIdToIncomingBlocksMap = (edges, mergeBlockIdsSet) =>
  reduce(
    edges,
    (acc, edge) => {
      const toBlockId = edge.to.block.id?.split('.')[0];
      if (!mergeBlockIdsSet.has(toBlockId)) {
        return acc;
      }
      if (!acc.has(toBlockId)) {
        acc.set(toBlockId, new Set());
      }
      const [blockId, blockPort] = edge.from.block.id.split('.');
      acc
        .get(toBlockId)
        .add(blockId)
        // blockPort handles continuations, e.g. Create Alert subtriggers:
        .add(blockPort);
      return acc;
    },
    new Map(),
  );

function getInvalidMergeInputs(planCanvas) {
  const { blocks, edges, inputs } = planCanvas;
  const mergeBlocks = filter(blocks, block => block.step?.type === MERGE);
  if (!mergeBlocks.length || !inputs?.length) {
    return null;
  }

  const allBlockIds = new Set(blocks.map(block => block.id));
  const mergeBlockIds = new Set(mergeBlocks.map(block => block.id));
  const edgeBlockIdsByMergeBlockId = getMergeBlockIdToIncomingBlocksMap(
    edges,
    mergeBlockIds,
  );
  const invalidMergeInputs = inputs.filter(input => {
    const { flowBlock, mergeOutput, inputPort } = input;
    if (!flowBlock || !mergeBlockIds.has(flowBlock.id) || !mergeOutput) {
      // not a relevant input so cannot be invalid by merge standards, filter out:
      return false;
    }
    if (!allBlockIds.has(inputPort)) {
      // Parent block does not exist - this is an invalid input:
      return true;
    }
    // Ensure input is from a parent (handle edge deletion):
    const incomingIds = edgeBlockIdsByMergeBlockId.get(flowBlock.id);
    return !incomingIds?.has(input.inputPort);
  });
  return new Set(invalidMergeInputs.map(input => input.id));
}

// Remove old inputs where the blocks no longer exist:
export function sanitizeMergeInputs(planCanvas) {
  const invalidMergeBlockInputIds = getInvalidMergeInputs(planCanvas);
  if (!invalidMergeBlockInputIds?.size) {
    return planCanvas;
  }

  return {
    ...planCanvas,
    inputs: planCanvas.inputs.filter(
      input => !invalidMergeBlockInputIds.has(input.id),
    ),
  };
}
